<template>
  <!-- Backdrop do drawer. Só existe em modo drawer (<1200px) e com ele aberto: no desktop o
       sidebar fica no fluxo e não há nada a escurecer. Fica atrás do .sidebar-area (z-index 99)
       e à frente de todo o resto. -->
  <div
    v-if="stateStoreInstance.drawerAberto"
    class="sidebar-backdrop"
    @click="fecharDrawer"
  ></div>

  <div
    ref="sidebarRef"
    :class="[
      'sidebar-area position-fixed start-0 top-0 bg-black h-100vh transition',
      { active: stateStoreInstance.open },
      { fullScreen: stateStoreInstance.isFullScreen }
    ]"
    id="sidebar-area"
    :role="stateStoreInstance.modoDrawer ? 'dialog' : null"
    :aria-label="stateStoreInstance.modoDrawer ? $t('Menu de navegação') : null"
    tabindex="-1"
  >
    <div class="logo position-absolute start-0 end-0 top-0 bg-black">
      <router-link to="/" class="d-flex align-items-center text-white text-decoration-none">
        <img src="../../../assets/logotipo.png" alt="logo-icon" />
        <span class="fw-bold ms-10">Trivenix</span>
      </router-link>
      <div class="border-bottom"></div>
      <button
        class="sidebar-burger-menu position-absolute lh-1 bg-transparent p-0 border-0"
        type="button"
        :aria-label="$t('Fechar menu')"
        @click="stateStoreInstance.onChange"
      >
        <i class="ph-duotone ph-caret-double-right"></i>
      </button>
    </div>
    <div class="sidebar-menu">
      <ul class="sidebar-navbar-nav ps-0 mb-0 list-unstyled accordion" id="sidebarNavAccordion">
        <!-- FILTRO DE BUSCA -->
        <li>
          <input
            v-if="!isSidebarCollapsed"
            :placeholder="$t('Buscar')"
            v-model="busca"
            @keydown.enter.prevent
            class="form-control form-control-sm mb-20"
            type="text"
          />
        </li>


        <!-- APPS -->
        <li class="sub-title sidebar-nav-item">
          <span class="d-block text-uppercase fw-medium">Apps</span>
        </li>

        <!-- Caso haja busca, exibe apenas as páginas filtradas e permitidas -->
        <ul v-if="busca" class="p-10 mb-0 list-unstyled">
          <li
            v-for="(page, i) in paginasFiltradasVisiveis"
            :key="i"
            class="sidebar-nav-item bg-transparent border-0 rounded-0"
          >
            <router-link
              :to="page.link"
              class="text-light sidebar-sub-menu-link text-decoration-none"
            >
              {{ page.name }}
              <small class="d-block text-muted">{{ page.folder }}</small>
            </router-link>
          </li>

          <li v-if="!paginasFiltradasVisiveis.length" class="text-muted">
            Nenhum resultado encontrado.
          </li>
        </ul>

        <!-- Caso não tenha buscas, exibir tudo -->
        <template v-else>
          <li
            v-for="(item, i) in menuFiltrado"
            :key="i"
            class="sidebar-nav-item accordion-item bg-transparent border-0 rounded-0"
          >
            <a
              href="#"
              class="accordion-button collapsed rounded-0 shadow-none bg-transparent d-block"
              data-bs-toggle="collapse"
              :data-bs-target="`#collapse-${i}`"
              aria-expanded="false"
              :aria-controls="`collapse-${i}`"
            >
              <i :class="item.icon"></i>
              <span class="title">{{ item.folder }}</span>
            </a>

            <div
              :id="`collapse-${i}`"
              class="accordion-collapse collapse"
              data-bs-parent="#sidebarNavAccordion"
            >
              <div class="accordion-body">
                <ul
                  v-for="(page, j) in item.pages"
                  :key="`page-${i}-${j}`"
                  class="sidebar-sub-menu ps-0 mb-0 list-unstyled"
                >
                  <li class="sidebar-sub-menu-item">
                    <router-link :to="page.link" class="sidebar-sub-menu-link">
                      {{ page.name }}
                    </router-link>
                  </li>
                </ul>

                <ul
                  v-for="(sub, k) in item.subfolder"
                  :key="`sub-${i}-${k}`"
                  class="sidebar-sub-menu ps-0 mb-0 list-unstyled"
                >
                  <li class="sidebar-sub-menu-item">
                    <a
                      href="#"
                      class="accordion-button collapsed rounded-0 shadow-none bg-transparent d-block"
                      data-bs-toggle="collapse"
                      :data-bs-target="`#collapse-sub-${i}-${k}`"
                      aria-expanded="false"
                      :aria-controls="`collapse-sub-${i}-${k}`"
                    >
                      <span class="title">{{ sub.folder }}</span>
                    </a>

                    <div :id="`collapse-sub-${i}-${k}`" class="accordion-collapse collapse">
                      <div class="accordion-body">
                        <ul
                          v-for="(page, l) in sub.pages"
                          :key="`page-${i}-${k}-${l}`"
                          class="sidebar-sub-menu ps-0 mb-0 list-unstyled"
                        >
                          <li class="sidebar-sub-menu-item">
                            <router-link :to="page.link" class="sidebar-sub-menu-link">
                              {{ page.name }}
                            </router-link>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup>
import stateStore from '@/utils/store'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { filtrarMenu, menu } from './menu.js'

const stateStoreInstance = stateStore
const route = useRoute()
const router = useRouter()
const sidebarRef = ref(null)

const fecharDrawer = () => stateStore.fecharDrawer()

// Fecha ao navegar: no drawer o menu cobre o conteúdo, então deixá-lo aberto depois do clique
// esconderia justamente a tela que o usuário acabou de pedir. Observar a rota (em vez de
// registrar router.afterEach) mantém a limpeza automática junto do ciclo do componente.
watch(() => route.fullPath, fecharDrawer)

const aoPressionarTecla = (evento) => {
  if (evento.key === 'Escape' && stateStore.drawerAberto) {
    fecharDrawer()
  }
}

// Enquanto o drawer está aberto, o scroll pertence a ele: sem isto o corpo da página rola
// atrás do menu no toque. A classe é aplicada no <body> porque é lá que o scroll acontece.
watch(
  () => stateStore.drawerAberto,
  (aberto, anterior) => {
    document.body.classList.toggle('drawer-open', aberto)

    if (aberto) {
      // Foco no drawer para que teclado e leitor de tela entrem no menu, e não continuem
      // no conteúdo que ficou atrás do backdrop.
      sidebarRef.value?.focus?.()
    } else if (anterior) {
      // Devolve o foco ao botão que abriu, senão ele volta para o início do documento.
      document.getElementById('header-burger-menu')?.focus?.()
    }
  }
)
const sidebarWidth = ref(null)

// A largura do estado colapsado é lida do token --sidebar-width-collapsed, não mais de um
// '60px' literal: o token é a fonte única da geometria do shell
// (assets/design-system/tokens/spacing.css) e muda por breakpoint. Comparação numérica com
// tolerância porque getComputedStyle devolve subpixel ('60.0031px') em telas com zoom.
const collapsedWidthPx = () => {
  const token = getComputedStyle(document.documentElement)
    .getPropertyValue('--sidebar-width-collapsed')
  return parseFloat(token)
}

const isSidebarCollapsed = computed(() => {
  const atual = parseFloat(sidebarWidth.value)
  const colapsado = collapsedWidthPx()
  if (Number.isNaN(atual) || Number.isNaN(colapsado)) return false
  return Math.abs(atual - colapsado) < 1
})

let observer = null

watch(isSidebarCollapsed, (collapsed) => {
  if (collapsed) {
    // Ao colapsar para o modo ícone o campo de busca deixa de ser visível: limpa o termo para
    // o menu não continuar filtrado por um texto que o usuário não vê mais.
    busca.value = ''
  }
})

onMounted(() => {
  document.addEventListener('keydown', aoPressionarTecla)

  const sidebar = document.querySelector('.sidebar-area')
  if (!sidebar) return

  // Observe sidebar size changes
  observer = new ResizeObserver(() => {
    sidebarWidth.value = window.getComputedStyle(sidebar).width
  })
  observer.observe(sidebar)
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
  document.removeEventListener('keydown', aoPressionarTecla)
  // O drawer pode estar aberto quando o layout é desmontado (troca para empty-layout, por
  // exemplo): sem isto o <body> ficaria travado sem scroll e sem nada que o destravasse.
  document.body.classList.remove('drawer-open')
})

const busca = ref('')

// Função para normalizar a busca
const match = (text, term) => text?.toLowerCase().includes(term.toLowerCase())

const paginasFiltradasVisiveis = computed(() => {
  const termo = busca.value.trim().toLowerCase()

  if (!termo) return []

  const paginas = []

  menuFiltrado.value.forEach((item) => {
    item.pages?.forEach((page) => {
      if (match(page.name, termo)) {
        paginas.push({ folder: item.folder, name: page.name, link: page.link })
      }
    })

    item.subfolder?.forEach((sub) => {
      sub.pages?.forEach((page) => {
        if (match(page.name, termo)) {
          paginas.push({ folder: `${item.folder} / ${sub.folder}`, name: page.name, link: page.link })
        }
      })
    })
  })

  return paginas
})

// Roles de cada página vêm da rota (meta.roles) — detalhes em ./menu.js.
const menuFiltrado = computed(() => filtrarMenu(menu, (link) => router.resolve(link)))
</script>
